import React, { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { GoodsReceiptRecord } from '@/types/domain/grn-management';
import { formatDate } from '@/lib/utils';
import { Printer, Download, Building2, PackageCheck, X } from 'lucide-react';

interface GrnPrintSlipModalProps {
  isOpen: boolean;
  onClose: () => void;
  grn: GoodsReceiptRecord | null;
}

export const GrnPrintSlipModal: React.FC<GrnPrintSlipModalProps> = ({
  isOpen,
  onClose,
  grn,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, onClose]);

  if (!isOpen || !grn) return null;

  const handlePrint = () => {
    window.print();
  };

  const watermarkText =
    grn.status === 'QUALITY_HOLD'
      ? 'QUALITY HOLD — QUARANTINED'
      : grn.status === 'REJECTED'
      ? 'REJECTED — RETURN TO VENDOR'
      : grn.status === 'CANCELLED'
      ? 'CANCELLED'
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl p-8 bg-card border border-border shadow-2xl z-10 space-y-6"
      >
        <div className="flex items-center justify-between pb-4 border-b border-border/60">
          <div>
            <h3 className="text-xl font-extrabold text-foreground flex items-center gap-2">
              <PackageCheck className="h-5 w-5 text-primary" /> Goods Receipt Note (GRN) Slip Preview
            </h3>
            <p className="text-xs text-foreground-secondary">
              Official warehouse inward receipt voucher and quality verification record.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-foreground-muted hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Printable Document Container */}
        <div id="printable-grn-document" className="relative p-6 bg-white text-slate-900 rounded-xl border border-slate-200 shadow-xs space-y-6 my-2">
          {/* Watermark for special statuses */}
          {watermarkText && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10 select-none">
              <span className="text-5xl font-black tracking-widest text-slate-950 -rotate-30 uppercase">
                {watermarkText}
              </span>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pb-6 border-b border-slate-200">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-primary font-black text-xl tracking-tight">
                <Building2 className="h-6 w-6" /> ProcureAI Global Enterprise Inc.
              </div>
              <p className="text-xs text-slate-500">Warehouse Receiving & Material Inwarding Division</p>
              <p className="text-xs text-slate-500">Plant: {grn.plantName} ({grn.plantCode})</p>
            </div>

            <div className="text-right space-y-1">
              <span className="text-2xl font-black text-slate-900 tracking-tight block">GOODS RECEIPT NOTE</span>
              <div className="text-sm font-bold text-slate-700">GRN #: <span className="text-primary font-black">{grn.grnNumber}</span></div>
              {grn.sapGrnNumber && (
                <div className="text-xs font-semibold text-purple-700">SAP MIGO Doc #: {grn.sapGrnNumber}</div>
              )}
              <div className="text-xs text-slate-500">Receipt Date: {formatDate(grn.receivedDate)}</div>
              <div className="text-xs text-slate-500">Challan / DC #: {grn.deliveryChallanNumber}</div>
            </div>
          </div>

          {/* PO & Vendor & Logistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
              <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">SUPPLIER / VENDOR</span>
              <p className="font-extrabold text-sm text-slate-900">{grn.vendorName}</p>
              <p className="text-slate-600 font-mono">Code: {grn.vendorCode}</p>
            </div>

            <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
              <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">PO COMMITMENT</span>
              <p className="font-extrabold text-sm text-primary font-mono">{grn.poNumber}</p>
              {grn.sapPoNumber && <p className="text-slate-600 font-mono">SAP PO: {grn.sapPoNumber}</p>}
            </div>

            <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
              <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">LOGISTICS & DOCK</span>
              <p className="text-slate-900 font-semibold">{grn.carrierName || 'Standard Freight'} ({grn.unloadingDock || 'Bay-1'})</p>
              <p className="text-slate-600 font-mono">Trk: {grn.trackingNumber || 'N/A'}</p>
              <p className="text-slate-600 font-mono">SLoc: {grn.storageLocation}</p>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-300 text-slate-600 font-extrabold uppercase text-[10px]">
                  <th className="py-2.5 px-2">#</th>
                  <th className="py-2.5 px-2">Material / SKU</th>
                  <th className="py-2.5 px-2 text-right">Ordered</th>
                  <th className="py-2.5 px-2 text-right">Received</th>
                  <th className="py-2.5 px-2 text-right text-rose-600">Rejected</th>
                  <th className="py-2.5 px-2 text-right text-emerald-700">Accepted</th>
                  <th className="py-2.5 px-2">UOM</th>
                  <th className="py-2.5 px-2">Batch #</th>
                  <th className="py-2.5 px-2">Quality</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {grn.items.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td className="py-2 px-2 font-mono text-slate-400">{item.lineNumber}</td>
                    <td className="py-2 px-2">
                      <span className="font-bold text-slate-900 block">{item.description}</span>
                      <span className="text-[10px] text-slate-500 font-mono">Code: {item.materialCode}</span>
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-slate-600">{item.orderedQuantity}</td>
                    <td className="py-2 px-2 text-right font-mono font-bold text-slate-900">{item.receivedQuantity}</td>
                    <td className="py-2 px-2 text-right font-mono font-bold text-rose-600">{item.rejectedQuantity}</td>
                    <td className="py-2 px-2 text-right font-mono font-black text-emerald-700">{item.acceptedQuantity}</td>
                    <td className="py-2 px-2 text-slate-600">{item.uom}</td>
                    <td className="py-2 px-2 font-mono text-slate-600">{item.batchNumber || 'N/A'}</td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded ${
                        item.qualityStatus === 'PASSED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : item.qualityStatus === 'FAILED'
                          ? 'bg-rose-100 text-rose-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {item.qualityStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Receipt Quantities Summary */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <div className="w-72 space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-600">
                <span>Total Physical Received:</span>
                <span className="font-mono font-bold">{grn.totalReceivedQuantity} units</span>
              </div>
              <div className="flex justify-between text-rose-600 font-semibold">
                <span>Total Rejected:</span>
                <span className="font-mono">- {grn.totalRejectedQuantity} units</span>
              </div>
              <div className="flex justify-between text-sm font-black text-emerald-800 pt-2 border-t border-slate-300">
                <span>Net Accepted into Stock:</span>
                <span className="font-mono">{grn.totalAcceptedQuantity} units</span>
              </div>
              <div className="flex justify-between text-xs text-slate-500 pt-1">
                <span>Unfulfilled PO Pending:</span>
                <span className="font-mono font-bold">{grn.totalPendingQuantity} units</span>
              </div>
            </div>
          </div>

          {/* Signatures & Certification */}
          <div className="pt-6 border-t border-slate-200 text-[10px] text-slate-500 space-y-2">
            <p className="font-bold text-slate-700">WAREHOUSE CERTIFICATION:</p>
            <p>
              I hereby certify that the goods listed above have been physically inspected, counted, and verified against PO #{grn.poNumber} and Delivery Challan #{grn.deliveryChallanNumber}. Accepted quantities have been taken on charge into the ERP inventory.
            </p>
            <div className="grid grid-cols-2 gap-8 pt-6 mt-4 border-t border-slate-200">
              <div>
                <div className="h-10 border-b border-slate-300 flex items-end font-script text-slate-700 italic">
                  {grn.receiverName}
                </div>
                <span className="text-[10px] font-bold text-slate-500 uppercase">Receiving Warehouse Officer Signature</span>
              </div>
              <div>
                <div className="h-10 border-b border-slate-300 flex items-end font-script text-slate-700 italic">
                  {grn.inspectorName}
                </div>
                <span className="text-[10px] font-bold text-slate-500 uppercase">Certified Quality Inspector Signature</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/40">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-xl font-bold border-border">
            Close Preview
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} className="rounded-xl font-bold border-border">
            <Printer className="h-4 w-4 mr-1.5" /> Print GRN Slip
          </Button>
          <Button size="sm" onClick={handlePrint} className="rounded-xl font-bold bg-primary text-white">
            <Download className="h-4 w-4 mr-1.5" /> Export PDF
          </Button>
        </div>
      </div>
    </div>
  );
};
